/**
 * EveryINC - Text Utils Module
 * Text normalization and mojibake repair utilities
 */

import type { User } from './types';

function looksLikeMojibake(text: string | null | undefined): boolean {
  return typeof text === 'string' && /\u00c3\u0192.|\u00c3\u201a.|\u00c3\u00a2.|\u00c3\u00b0\u0160\u0178|\ufffd/.test(text);
}

function repairMojibakeText(text: string): string {
  if (!looksLikeMojibake(text)) return text;

  let repaired = text;

  for (let i = 0; i < 3; i += 1) {
    try {
      const bytes = Uint8Array.from(repaired, char => char.charCodeAt(0) & 0xff);
      const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);

      if (!decoded || decoded === repaired) break;
      repaired = decoded;

      if (!looksLikeMojibake(repaired)) break;
    } catch {
      break;
    }
  }

  const commonReplacements: [string, string][] = [
    ['\u00c3\u0192\u00c2\u00a1', '\u00e1'],
    ['\u00c3\u0192 ', '\u00e0'],
    ['\u00c3\u0192\u00c2\u00a2', '\u00e2'],
    ['\u00c3\u0192\u00c2\u00a3', '\u00e3'],
    ['\u00c3\u0192\u00c2\u00a4', '\u00e4'],
    ['\u00c3\u0192\u00c2\u00a7', '\u00e7'],
    ['\u00c3\u0192\u00c2\u00a9', '\u00e9'],
    ['\u00c3\u0192\u00c2\u00aa', '\u00ea'],
    ['\u00c3\u0192\u00c2\u00ad', '\u00ed'],
    ['\u00c3\u0192\u00c2\u00b3', '\u00f3'],
    ['\u00c3\u0192\u00c2\u00b4', '\u00f4'],
    ['\u00c3\u0192\u00c2\u00b5', '\u00f5'],
    ['\u00c3\u0192\u00c2\u00ba', '\u00fa'],
    ['\u00c3\u0192\u00e2\u20ac\u2260', '\u00c9'],
    ['\u00c3\u0192\u00e2\u20ac\u0153', '\u00d3'],
    ['\u00c3\u0192\u00e2\u20ac\u00a1', '\u00c7'],
    ['\u00c3\u0192\u00c6\u2019\u00c3\u201a\u00c2\u00a1', '\u00e1'],
    ['\u00c3\u0192\u00c6\u2019\u00c3\u201a\u00c2\u00a3', '\u00e3'],
    ['\u00c3\u0192\u00c6\u2019\u00c3\u201a\u00c2\u00a7', '\u00e7'],
    ['\u00c3\u0192\u00c6\u2019\u00c3\u201a\u00c2\u00a9', '\u00e9'],
    ['\u00c3\u0192\u00c6\u2019\u00c3\u201a\u00c2\u00b3', '\u00f3'],
    ['\u00c3\u0192\u00c6\u2019\u00c3\u201a\u00c2\u00b5', '\u00f5'],
    ['\u00c3\u0192\u00c6\u2019\u00c3\u201a\u00c2\u00ba', '\u00fa'],
    ['\u00c3\u00a2\u00e2\u201a\u00ac \u00e2\u201e\u00a2', '\u2192'],
    ['\u00c3\u00a2\u00e2\u201a\u00ac\u00e2\u20ac\u0153', '\u2014'],
    ['\u00c3\u00a2\u00e2\u201a\u00ac\u00c2\u00a6', '\u2026']
  ];

  commonReplacements.forEach(([broken, fixed]) => {
    repaired = repaired.split(broken).join(fixed);
  });

  return repaired;
}

interface NodeFilterWithAcceptNode {
  acceptNode(node: Node): number;
}

function normalizeUiText(root: HTMLElement | null = document.body): void {
  if (!root) return;

  const attributesToFix = ['placeholder', 'title', 'aria-label', 'alt'];
  const textWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node: Node): number {
      const parentTag = (node as Text).parentElement?.tagName;
      if (!node.nodeValue?.trim()) return NodeFilter.FILTER_REJECT;
      if (parentTag === 'SCRIPT' || parentTag === 'STYLE') return NodeFilter.FILTER_REJECT;
      return looksLikeMojibake(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    }
  } as NodeFilterWithAcceptNode);

  const textNodes: Text[] = [];
  while (textWalker.nextNode()) {
    textNodes.push(textWalker.currentNode as Text);
  }

  textNodes.forEach(node => {
    if (node.nodeValue) {
      node.nodeValue = repairMojibakeText(node.nodeValue);
    }
  });

  const elements = root instanceof Element ? [root, ...Array.from(root.querySelectorAll('*'))] : [];
  elements.forEach(element => {
    attributesToFix.forEach(attribute => {
      const value = element.getAttribute?.(attribute);
      if (looksLikeMojibake(value)) {
        element.setAttribute(attribute, repairMojibakeText(value!));
      }
    });
  });
}

function normalizeCurrentUser(user: User | null): User | null {
  if (!user) return null;

  const displayName = [
    user.first_name,
    user.last_name
  ].filter(Boolean).join(' ').trim() || user.name || 'Usuario';

  return {
    ...user,
    name: displayName
  };
}

const EveryIncText = {
  looksLikeMojibake,
  repairMojibakeText,
  normalizeUiText,
  normalizeCurrentUser
};

// Attach to window for IIFE compatibility
(window as Window).EveryIncText = EveryIncText;

export {
  looksLikeMojibake,
  repairMojibakeText,
  normalizeUiText,
  normalizeCurrentUser
};

export default EveryIncText;
